/**
 * Registry of DICOM requests currently on the wire, so a caller that gave up
 * on a load can abort the underlying socket. That is the ONLY way to make the
 * loader forget a request: the wadouri dataSetCacheManager hands the same
 * pending promise back to every later load of a URL, so without the abort a
 * "retry" just re-awaits the hung request it gave up on.
 *
 * Dependency-free on purpose (no Cornerstone imports): mprSetup calls
 * abortInFlightImageRequests on teardown, and must not pull
 * `@cornerstonejs/dicom-image-loader` into its graph. registerFallback — the
 * DICOM loader's `open` hook — is the only writer.
 */
import { markDeliberateAbort } from './abortedRequests';
/** Live requests keyed by the URL the wadouri loader handed to `open` (the
 * imageId minus its scheme and `frame=` suffix — one object backs many frame
 * imageIds). */
const inFlight = new Map();
/** Register `xhr` (just opened for `url`) until its `loadend`. */
export function trackInFlight(url, xhr) {
    let set = inFlight.get(url);
    if (!set) {
        set = new Set();
        inFlight.set(url, set);
    }
    set.add(xhr);
    xhr.addEventListener('loadend', () => {
        const live = inFlight.get(url);
        if (!live)
            return;
        live.delete(xhr);
        if (live.size === 0)
            inFlight.delete(url);
    });
}
/** The wadouri loader's request URL for an imageId (mirrors its parseImageId). */
function requestUrlForImageId(imageId) {
    const url = imageId.replace(/^wadouri:/, '');
    const frameIndex = url.indexOf('frame=');
    return frameIndex === -1 ? url : url.substring(0, frameIndex - 1);
}
/**
 * Abort every in-flight request behind `imageIds`. Returns the subset of
 * `imageIds` whose request was aborted (every frame id sharing an aborted
 * object's URL). The aborts are flagged as deliberate so neither the LAN
 * circuit breaker nor Sentry counts them as failures.
 */
export function abortInFlightImageRequests(imageIds) {
    const abortedUrls = new Set();
    for (const url of new Set(imageIds.map(requestUrlForImageId))) {
        const live = inFlight.get(url);
        if (!live)
            continue;
        for (const xhr of [...live]) {
            markDeliberateAbort(xhr);
            xhr.abort();
        }
        abortedUrls.add(url);
    }
    return imageIds.filter((id) => abortedUrls.has(requestUrlForImageId(id)));
}
/** Test-only. */
export function resetInFlightRequestsForTests() {
    inFlight.clear();
}
